import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Zap, Save, PlayCircle, ShieldCheck, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

/**
 * Automation tab — configurable rules that turn AI recommendations into
 * automatic actions after a grace period.
 *
 * Rules currently supported:
 *   • auto_reassign_orphans — hand off orphaned assets to the most
 *     obvious candidate owner after `grace_days` days.
 *   • auto_archive_stale — soft-archive stale content after `grace_days`
 *     of continued inactivity.
 */
export default function AutomationTab({ active }) {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.getAutomation();
      setData(res);
      setDirty(false);
    } catch (e) {
      toast.error(e.message || 'Failed to load automation rules');
    }
  }, []);

  useEffect(() => { if (active && !data) load(); }, [active, data, load]);

  const patch = (key, field, value) => {
    setData(d => ({
      ...d,
      rules: { ...d.rules, [key]: { ...d.rules[key], [field]: value } },
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        auto_reassign_orphans: data.rules.auto_reassign_orphans,
        auto_archive_stale: data.rules.auto_archive_stale,
      };
      await api.updateAutomation(payload);
      toast.success('Automation rules saved');
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await api.runAutomationNow();
      toast.success(`Applied — ${res.reassigned} reassigned, ${res.archived} archived`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  if (!data) return <div className="p-4 text-sm text-slate-500" data-testid="automation-loading">Loading…</div>;

  const r = data.rules;
  const recent = data.recent_log || [];

  return (
    <div className="space-y-6" data-testid="governance-automation">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={16} className="text-amber-400" />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Automation rules</h3>
          </div>
          <p className="text-sm text-slate-500">
            Turn AI recommendations into automatic actions after a grace period. Owners are notified before their content is touched.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={runNow} disabled={running} data-testid="automation-run-now-btn">
            <PlayCircle size={14} className="mr-1" />
            {running ? 'Running…' : 'Run once now'}
          </Button>
          <Button size="sm" onClick={save} disabled={saving || !dirty} data-testid="automation-save-btn">
            <Save size={14} className="mr-1" />
            {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>

      {/* Rule 1: Auto-reassign orphans */}
      <RuleCard
        testId="rule-auto-reassign"
        icon={ShieldCheck}
        title="Auto-reassign orphans"
        description="When an asset's owner is deactivated and one candidate has clearly dominant engagement, transfer ownership automatically."
        cfg={r.auto_reassign_orphans}
        onChange={(f, v) => patch('auto_reassign_orphans', f, v)}
        fields={[
          { key: 'grace_days',     label: 'Grace period (days)', type: 'number', min: 1, max: 60, help: 'Wait this long before applying' },
          { key: 'min_score',      label: 'Minimum candidate score', type: 'number', min: 1, max: 50, help: 'Interactions with the asset in last 60 days' },
          { key: 'min_dominance',  label: 'Dominance multiplier', type: 'number', min: 1, max: 10, step: 0.5, help: 'Top candidate must be Nx better than #2' },
          { key: 'notify_new_owner', label: 'Email the new owner', type: 'toggle' },
        ]}
      />

      {/* Rule 2: Auto-archive stale */}
      <RuleCard
        testId="rule-auto-archive"
        icon={Archive}
        title="Auto-archive stale content"
        description="If a stale-flagged asset keeps receiving no traffic, move it to the archive to keep the workspace tidy."
        cfg={r.auto_archive_stale}
        onChange={(f, v) => patch('auto_archive_stale', f, v)}
        fields={[
          { key: 'grace_days', label: 'Grace period (days)', type: 'number', min: 1, max: 90, help: 'Additional days after flagging' },
          { key: 'min_severity', label: 'Minimum severity', type: 'select', options: ['info', 'warning', 'critical'] },
          { key: 'notify_owner', label: 'Email the owner', type: 'toggle' },
        ]}
      />

      {recent.length > 0 && (
        <div>
          <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Recent automated actions</h4>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Rule</th>
                  <th className="px-3 py-2 text-left">Action</th>
                  <th className="px-3 py-2 text-left">Asset</th>
                </tr>
              </thead>
              <tbody>
                {recent.slice(0, 25).map((e, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2 text-slate-500 text-xs">{new Date(e.applied_at).toLocaleString()}</td>
                    <td className="px-3 py-2"><code className="text-xs">{e.kind}</code></td>
                    <td className="px-3 py-2 capitalize">{e.action}</td>
                    <td className="px-3 py-2 truncate max-w-xs">{e.object_name || e.object_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RuleCard({ testId, icon: Icon, title, description, cfg, onChange, fields }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${cfg?.enabled ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
            <Icon size={16} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-800 dark:text-slate-200">{title}</div>
            <div className="text-sm text-slate-500 mt-0.5">{description}</div>
          </div>
        </div>
        <button
          onClick={() => onChange('enabled', !cfg?.enabled)}
          data-testid={`${testId}-toggle`}
          className={`shrink-0 relative w-11 h-6 rounded-full transition-colors ${cfg?.enabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'}`}
          aria-label={cfg?.enabled ? 'Disable' : 'Enable'}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${cfg?.enabled ? 'translate-x-5' : ''}`} />
        </button>
      </div>

      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${cfg?.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
        {fields.map(f => (
          <div key={f.key}>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">{f.label}</label>
            {f.type === 'number' && (
              <input
                type="number"
                min={f.min}
                max={f.max}
                step={f.step || 1}
                value={cfg?.[f.key] ?? ''}
                onChange={(e) => onChange(f.key, Number(e.target.value))}
                data-testid={`${testId}-${f.key}`}
                className="w-full px-3 py-2 text-sm rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
              />
            )}
            {f.type === 'select' && (
              <select
                value={cfg?.[f.key] || ''}
                onChange={(e) => onChange(f.key, e.target.value)}
                data-testid={`${testId}-${f.key}`}
                className="w-full px-3 py-2 text-sm rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
              >
                {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {f.type === 'toggle' && (
              <button
                onClick={() => onChange(f.key, !cfg?.[f.key])}
                data-testid={`${testId}-${f.key}`}
                className={`px-3 py-2 rounded-md text-xs border ${cfg?.[f.key] ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800' : 'bg-slate-50 dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700'}`}
              >
                {cfg?.[f.key] ? 'Enabled' : 'Disabled'}
              </button>
            )}
            {f.help && <p className="text-xs text-slate-400 mt-1">{f.help}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
